/**
 * 重新登录 / 切换机构期间的「鉴权空窗期」保护。
 *
 * 换发登录态时会先清掉本地旧鉴权，再经过若干次网络请求才把新鉴权写回。localStorage 是
 * 多标签页共享的，这段空窗期里同浏览器其它已打开的页面（版本轮询、预约提醒、空闲检测或
 * 用户操作）读到的是空 token，请求必然被后端判为登录失效，于是弹出「请重新登录」把用户
 * 又赶回登录页一次 —— 而此时会话其实是好的，只是新 token 还没写完。
 *
 * 本模块在换发开始前写入一个带时间戳的标记，供请求层在收到登录失效码时判定「这不是真的
 * 掉登录，只是本机正在换发」从而跳过弹框。标记带 TTL 兜底，避免换发流程中断（例如用户
 * 停在选择机构弹窗上）后永久屏蔽真实的登录失效提示。
 *
 * @module utils/authTransition
 */
import { LOCAL_STORAGE_KEY } from '@/constants';
import { storage } from '@/utils/storageOperateApi';

/** 标记最长有效时间，超时视为换发流程已中断 */
const AUTH_TRANSITION_TTL_MS = 20000;

/**
 * 标记本机进入登录态换发流程，必须在清空本地鉴权之前调用。
 * @param {string} [reason] - 换发来源，仅用于排查日志
 * @returns {void}
 * @example
 * beginAuthTransition('login');
 * storage.clear();
 */
export function beginAuthTransition(reason = '') {
  // isClear 传 false：标记要挺过换发流程里的 storage.clear()，否则刚写就被清掉
  storage.set(LOCAL_STORAGE_KEY.AUTH_TRANSITION, { at: Date.now(), reason }, null, false, false);
}

/**
 * 标记换发流程结束，须在新鉴权写入本地之后调用。
 * @returns {void}
 */
export function endAuthTransition() {
  storage.remove(LOCAL_STORAGE_KEY.AUTH_TRANSITION);
}

/**
 * 本机是否正处于登录态换发流程中。
 * @returns {boolean} 处于换发中且未超时返回 true
 */
export function isAuthTransitioning() {
  const mark = storage.get(LOCAL_STORAGE_KEY.AUTH_TRANSITION);
  if (!mark?.at) return false;
  // 超时说明换发流程已中断，清掉标记恢复正常的登录失效提示
  if (Date.now() - mark.at > AUTH_TRANSITION_TTL_MS) {
    endAuthTransition();
    return false;
  }
  return true;
}
